/**
 * Course route segment helpers for the courses routing layer only.
 * Kept local so this layer does not depend on gokollab discovery/search layers.
 */

/** Mongo ObjectId or UUID — checkout product id, not a discovery category slug. */
export function isCourseCheckoutProductId(segment: string): boolean {
  if (/^[a-f0-9]{24}$/i.test(segment)) return true
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(segment)
}

/**
 * Reserved first segments under `/courses/{segment}` owned by the native courses app.
 * Keep in sync with `gokollab-route.constants.ts` (`COURSES_NON_DISCOVERY_SEGMENTS`).
 */
const COURSES_NON_DISCOVERY_SEGMENTS = new Set([
  'my-library',
  'search',
  'products',
  'groups',
  'category',
  'my-courses',
  'discover',
  'bundles',
  'upsell-offers',
])

function getCoursesPathSegment(path: string): string | null {
  const match = path.match(/^\/courses\/([^/]+)/)
  return match?.[1] ?? null
}

/**
 * Paths owned by the GoKollab app discovery/search surface (by route name or path).
 * Courses access guard must not treat these as product lesson routes.
 */
export function shouldSkipCoursesAccessGuardForGokollabRoute(path: string, routeName: string | symbol | null | undefined): boolean {
  const name = routeName == null ? '' : String(routeName)

  if (
    name.startsWith('gokollab-community') ||
    name.startsWith('gokollab-courses') ||
    name.startsWith('gokollab-creators') ||
    name === 'gokollab-search-input'
  ) {
    return true
  }

  if (path === '/search-input') {
    return true
  }

  if (/^\/community\/[^/]+(\/search)?$/.test(path)) {
    return true
  }

  if (/^\/creator\/[^/]+$/.test(path)) {
    return true
  }

  if (/^\/creators\/[^/]+$/.test(path)) {
    return true
  }

  const segment = getCoursesPathSegment(path)
  if (!segment) {
    return false
  }

  if (COURSES_NON_DISCOVERY_SEGMENTS.has(segment)) {
    return false
  }

  if (isCourseCheckoutProductId(segment)) {
    return false
  }

  return /^\/courses\/[^/]+(\/search)?$/.test(path)
}
